import React from 'react';
import { reportClientError } from '../services/clientErrorReporter';

const CHUNK_RELOAD_GUARD = 'fs_chunk_reload_once';

const isChunkLoadFailure = (error) => {
  const message = typeof error === 'string' ? error : error?.message || String(error || '');
  return (
    message.includes('ChunkLoadError') ||
    message.includes('Loading chunk') ||
    message.includes('failed dynamically imported module') ||
    message.includes('Failed to fetch dynamically imported module') ||
    (message.includes('module script') && message.includes('text/html'))
  );
};

const clearRuntimeCaches = async () => {
  try {
    if ('caches' in window) {
      const names = await window.caches.keys();
      await Promise.all(names.map((name) => window.caches.delete(name)));
    }
  } catch (_) {
    // Best effort only; a reload still helps if Cache API is unavailable.
  }
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Report first: chunk failures (bad deploys) and render crashes are the
    // highest-value signals, and the chunk path below reloads the page.
    reportClientError(error, {
      source: 'boundary',
      componentStack: errorInfo?.componentStack,
    });

    if (isChunkLoadFailure(error)) {
      try {
        const errorStr = error?.message || String(error || '');
        window.localStorage.setItem('fs_reload_reason', JSON.stringify({
          error: `ErrorBoundary: ${errorStr}`,
          time: new Date().toISOString(),
          url: window.location.href
        }));
      } catch (_) {}

      try {
        if (window.sessionStorage.getItem(CHUNK_RELOAD_GUARD) !== '1') {
          window.sessionStorage.setItem(CHUNK_RELOAD_GUARD, '1');
          clearRuntimeCaches().finally(() => window.location.reload());
          return;
        }
      } catch (_) {
        window.location.reload();
        return;
      }
    }
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#0f0f23] text-white flex items-center justify-center p-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
            <p className="text-gray-400 mb-4">The app encountered an error and couldn't load properly.</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-[#8b5cf6] hover:bg-[#7c3aed] text-white px-4 py-2 rounded-lg"
            >
              Reload Page
            </button>
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-sm text-gray-400">Error Details</summary>
              <pre className="mt-2 text-xs text-red-400 bg-black p-2 rounded overflow-auto">
                {this.state.error?.toString()}
              </pre>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 
